<template>
  <FullScreenModal :open="open" @close="closeModal">
    <div>
      <h1 class="flex items-center justify-center text-lg sm:text-2xl text-center font-bold">Ajout du devis</h1>
      <p class="mt-2 sm:mt-4 text-sm text-gray-600">
        Votre sinistre a été approuvé pour une réparation. Détaillez les coûts et déposez un devis proforma pour que nos
        équipes puissent le traiter.
      </p>
    </div>
    <div class="mt-5 sm:mt-7 border-t border-gray-200 pt-5 sm:pt-7">
      <p class="text-sm font-semibold text-gray-900">Le produit est-il réparable ?</p>
      <div class="flex flex-col sm:flex-row mt-3 gap-3">
        <label
          :class="[
            'flex items-center gap-2 px-4 py-3 rounded-lg border cursor-pointer transition-colors text-sm',
            isProductFixable === true
              ? 'border-primary bg-primary-light ring-1 ring-primary'
              : 'border-gray-200 hover:bg-gray-50',
          ]"
        >
          <input
            name="product-fixeable"
            type="radio"
            class="h-4 w-4 border-gray-300 text-primary-dark focus:ring-primary"
            :value="true"
            v-model="isProductFixable"
          />
          <span>Oui, le produit est réparable</span>
        </label>
        <label
          :class="[
            'flex items-center gap-2 px-4 py-3 rounded-lg border cursor-pointer transition-colors text-sm',
            isProductFixable === false
              ? 'border-primary bg-primary-light ring-1 ring-primary'
              : 'border-gray-200 hover:bg-gray-50',
          ]"
        >
          <input
            name="product-not-fixeable"
            type="radio"
            class="h-4 w-4 border-gray-300 text-primary-dark focus:ring-primary"
            :value="false"
            v-model="isProductFixable"
          />
          <span>Non, le produit n'est pas réparable</span>
        </label>
      </div>
    </div>
    <div v-if="isProductFixable === true" class="border-t border-gray-200 pt-5 sm:pt-7 mt-5 sm:mt-7">
      <FixeableProductProcess
        :extract-cost-items="extractCostItemsFromFile"
        @update="
          (newProductState, newFile, newcostItems) => {
            productState = newProductState;
            file = newFile;
            costItems = newcostItems;
          }
        "
      />
    </div>
    <div v-if="isProductFixable === false" class="border-t border-gray-200 pt-5 sm:pt-7 mt-5 sm:mt-7">
      <Alert type="warning" class="mb-4">
        En cas de remplacement pour produit irréparable, le produit devient la propriété d'Estaly et sera récupéré par
        nos équipes.
      </Alert>
      <FormField
        v-model="productNotFixableReason"
        type="text"
        label="Raison de l'irréparabilité"
        name="nonFixableReason"
        id="nonFixableReason"
        required
      />
      <FormField
        class="mt-4"
        id="non-repairability-certificate"
        v-model="irreparabilityCertificate"
        required
        type="file"
        label="Attestation d'irréparabilité"
        field-class="block w-full text-sm text-gray-700 border border-gray-300 rounded-lg cursor-pointer bg-gray-50 focus:outline-hidden"
        name="non-repairability-certificate"
      />
    </div>
    <PricingDetails :claim="claim" :costItems="costItems" />
    <CostItemsConfirmation
      v-if="isProductFixable && costItems && costItems.length > 0"
      :confirmed="itemsConfirmed"
      :total-amount="totalTTC"
      document-type="quotation"
      @confirm="itemsConfirmed = true"
    />
    <div v-if="validForm" class="mt-7 flex justify-center text-sm">
      <GenericButton @click="send">Valider et envoyer</GenericButton>
    </div>
  </FullScreenModal>
</template>

<script>
import {
  FullScreenModal,
  GenericButton,
  FormField,
  Alert,
  PricingDetails,
  FixeableProductProcess,
  CostItemsConfirmation,
  computeTotalTTC,
  isPresent,
} from "@estaly/ui";
import { mapActions } from "vuex";

export default {
  components: {
    Alert,
    FormField,
    GenericButton,
    FixeableProductProcess,
    FullScreenModal,
    PricingDetails,
    CostItemsConfirmation,
  },
  props: ["claim", "open"],
  computed: {
    totalTTC() {
      return computeTotalTTC(this.costItems);
    },
    validForm() {
      const productFixableInformationsValid =
        this.isProductFixable &&
        isPresent(this.file) &&
        isPresent(this.costItems) &&
        isPresent(this.productState) &&
        this.itemsConfirmed;
      const productNonFixableInformationsValid =
        !this.isProductFixable && isPresent(this.productNotFixableReason) && isPresent(this.irreparabilityCertificate);

      if (productFixableInformationsValid || productNonFixableInformationsValid) return true;

      return false;
    },
  },
  data: () => {
    return {
      file: null,
      costItems: null,
      isProductFixable: null,
      productState: null,
      productNotFixableReason: null,
      irreparabilityCertificate: null,
      itemsConfirmed: false,
    };
  },
  watch: {
    costItems() {
      this.itemsConfirmed = false;
    },
    isProductFixable(newValue) {
      if (newValue === true) {
        this.productNotFixableReason = null;
        this.irreparabilityCertificate = null;
      } else if (newValue === false) {
        this.file = null;
        this.costItems = null;
        this.productState = null;
      }
    },
  },
  methods: {
    ...mapActions("notifications", ["notify"]),
    ...mapActions("claims", ["uploadQuotation", "declareIrreparable", "extractCostItems"]),

    async extractCostItemsFromFile(file) {
      return await this.extractCostItems({ file });
    },
    async send() {
      if (this.isProductFixable) {
        const response = await this.uploadQuotation({
          costItems: this.costItems,
          file: this.file,
          productState: this.productState,
        });
        if (response?.success) this.closeModal();
      } else {
        const response = await this.declareIrreparable({
          message: "Produit non réparable",
          reason: this.productNotFixableReason,
          irreparabilityCertificate: this.irreparabilityCertificate,
        });
        if (response?.success) this.closeModal();
      }
    },
    closeModal() {
      this.$emit("close");
      this.file = null;
      this.costItems = null;
      this.isProductFixable = null;
      this.productNotFixableReason = null;
      this.itemsConfirmed = false;
    },
  },
};
</script>
